import { ConcurrentResource,
  ConcurrentResourceRegistry,
  ConcurrentResourceRegistryByType,
  ConcurrentResourceType } from './concurrent-resource.model';
import { RealTimeService } from '../../services/realtime.service';
import { Injectable } from '@angular/core';
import { BehaviorSubject, map, mergeMap, Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { UserSessionRegistryService } from '../user-session-registry/user-session-registry.service';

@Injectable({ providedIn: 'root' })
export class ConcurrentResourceRegistryService {

  private static REGISTRY_PATH = 'locked-concurrent-resource-registry';
  private static LOCK_PATH = 'lock-concurrent-resources';
  private static UNLOCK_PATH = 'unlock-concurrent-resources';

  private _registryBehaviorSubject = new BehaviorSubject<ConcurrentResourceRegistryByType>({});
  public registry$ = this._registryBehaviorSubject.asObservable();

  get registry() {
    return this._registryBehaviorSubject.value;
  }

  constructor(private readonly realtimeService: RealTimeService,
              private readonly connectedUserSessionRegistryService: UserSessionRegistryService,
              private readonly httpClient: HttpClient
  ) {
    this.init();
  }

  private init() {
    this.realtimeService.watch<ConcurrentResourceRegistryByType>(ConcurrentResourceRegistryService.REGISTRY_PATH)
      .subscribe(registry => this._registryBehaviorSubject.next(registry ?? {}));

    this.realtimeService.isConnected$()
      .pipe(mergeMap(() => this.getRegistry()))
      .subscribe(registry => this._registryBehaviorSubject.next(registry ?? {}));
  }

  public getRegistryByType(type: ConcurrentResourceType): ConcurrentResourceRegistry {
    return this._registryBehaviorSubject.value ? this._registryBehaviorSubject.value[type] : {};
  }

  public getResourcesLockedByType$(type: ConcurrentResourceType, onlyLockedByOthers: boolean = true): Observable<ConcurrentResourceRegistry> {
    return this.registry$.pipe(map(registry => {
      if (!registry || !registry[type]) {
        return {};
      }

      if (!onlyLockedByOthers) {
        return registry[type];
      }

      return Object.entries(registry[type])
        .filter(([appointmentLockedUuid, resourceLocker]) => !this.connectedUserSessionRegistryService.isMe(resourceLocker.stompSessionId))
        .reduce((acc, [key, value]) => {
          acc[key] = value;
          return acc;
        }, {} as ConcurrentResourceRegistry);
    }));
  }

  public lock(...resourcesLocked: ConcurrentResource[]): void {
    this.realtimeService.sendDataOnStomp(resourcesLocked, ConcurrentResourceRegistryService.LOCK_PATH);
  }

  public unlock(...resourcesLocked: ConcurrentResource[]) {
    this.realtimeService.sendDataOnStomp(resourcesLocked, ConcurrentResourceRegistryService.UNLOCK_PATH);
  }


  private getRegistry(): Observable<ConcurrentResourceRegistryByType> {
    return this.httpClient.get<ConcurrentResourceRegistryByType>(`${environment.api_base_url}${ConcurrentResourceRegistryService.REGISTRY_PATH}`);
  }
}
